import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { Link, useNavigate, useParams } from 'react-router-dom'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import Loading from '../../loading/loadingPage'
import { DetailRow } from '../../../components/Cards/DetailsBox'
import AnDateField from '../../../components/DateTime/DateField'
import EmployeeBaseLocationsSelect from '../../../components/EmployeeBaseLocationsSelect'

import ArchiveIcon from '@mui/icons-material/Archive'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BlockIcon from '@mui/icons-material/Block'
import EmailIcon from '@mui/icons-material/Email'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import RefreshIcon from '@mui/icons-material/Refresh'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

const statusColor = (subcontractor) => {
  if (subcontractor.status === 'Active') return 'success'
  if (subcontractor.status === 'Pending Verification') return 'warning'
  if (subcontractor.status === 'Archived') return 'default'
  return 'error'
}

const DURATION_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 3, label: '3 Days' },
  { value: 7, label: '1 Week' },
  { value: 14, label: '2 Weeks' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 0, label: 'Indefinite - never expires' },
]

const EMPLOYMENT_TYPE_OPTIONS = ['PAYE', 'Invoice']

const formatDate = (date) =>
  date ? moment(date).format('DD MMM YYYY HH:mm') : '-'

const fullName = (user) =>
  user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '-'

const SubcontractorSingle = () => {
  const { subcontractorId } = useParams()
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const { orgConfig } = useContext(TenantSettingsContext)
  const { setPageTitle } = useDocumentTitle()
  const roles = orgConfig?.orgSettings?.roles ?? []
  const defaultWindowDays =
    orgConfig?.orgSettings?.subcontractorSettings?.accessWindowDays ?? 7

  const [subcontractor, setSubcontractor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [grantOpen, setGrantOpen] = useState(false)
  const [grantDays, setGrantDays] = useState(
    DURATION_OPTIONS.some((o) => o.value === defaultWindowDays)
      ? defaultWindowDays
      : 7,
  )
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [clinicalRole, setClinicalRole] = useState('')

  const [convertOpen, setConvertOpen] = useState(false)
  const [conversionPreview, setConversionPreview] = useState(null)
  const [converting, setConverting] = useState(false)
  const [conversion, setConversion] = useState({})
  const [conversionBaseError, setConversionBaseError] = useState(null)

  useEffect(() => {
    if (subcontractor) {
      setPageTitle(
        subcontractor?.userAccount?.firstName + ' ' + subcontractor?.userAccount?.lastName,
      )
    }
  }, [subcontractor, setPageTitle])

  useEffect(() => {
    API.get(`/subcontractors/${subcontractorId}`)
      .then((res) => {
        if (res.status === 200) {
          setSubcontractor(res.data)
          setClinicalRole(res.data.clinicalRole ?? '')
        } else {
          setNotFound(true)
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [subcontractorId])

  const applyUpdate = (updated, message) => {
    setSubcontractor(updated)
    setClinicalRole(updated.clinicalRole ?? '')
    if (message) setToast({ message })
  }

  const grantAccess = () => {
    API.post(`/subcontractors/${subcontractorId}/reactivate`, {
      days: grantDays,
    }).then((res) => {
      if (res.status === 200) {
        setGrantOpen(false)
        applyUpdate(
          res.data,
          grantDays === 0
            ? 'Access granted indefinitely'
            : `Access granted for ${
                DURATION_OPTIONS.find((o) => o.value === grantDays)?.label ??
                `${grantDays} days`
              }`,
        )
      } else {
        setToast({ severity: 'error', message: 'Unable to grant access' })
      }
    })
  }

  const revokeAccess = () => {
    API.post(`/subcontractors/${subcontractorId}/revoke`).then((res) => {
      setRevokeOpen(false)
      if (res.status === 200) {
        applyUpdate(res.data, 'Access revoked')
      } else {
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to revoke access',
        })
      }
    })
  }

  const saveClinicalRole = () => {
    API.put(`/subcontractors/${subcontractorId}`, { clinicalRole }).then(
      (res) => {
        if (res.status === 200) {
          applyUpdate(res.data, 'Clinical role updated')
        } else {
          setToast({
            severity: 'error',
            message: 'Unable to update clinical role',
          })
        }
      },
    )
  }

  const archive = () => {
    API.post(`/subcontractors/${subcontractorId}/archive`).then((res) => {
      if (res.status === 200) applyUpdate(res.data, 'Subcontractor archived')
    })
  }

  const unarchive = () => {
    API.post(`/subcontractors/${subcontractorId}/unarchive`).then((res) => {
      if (res.status === 200) applyUpdate(res.data, 'Subcontractor restored')
    })
  }

  const openConvert = () => {
    setConversion({
      clinicalRole: subcontractor.clinicalRole ?? '',
      employmentStartDate: moment(),
      employmentType: '',
      permittedBaseLocations: [],
      sendEmail: true,
    })
    setConversionBaseError(null)
    setConversionPreview(null)
    setConvertOpen(true)
    API.get(`/subcontractors/${subcontractorId}/conversion-preview`).then(
      (res) => {
        if (res.status === 200) setConversionPreview(res.data)
      },
    )
  }

  const convertToEmployee = () => {
    setConverting(true)
    API.post(`/subcontractors/${subcontractorId}/convert-to-employee`, {
      ...conversion,
      employmentStartDate: conversion.employmentStartDate?.isValid?.()
        ? conversion.employmentStartDate.toISOString()
        : undefined,
      employmentType: conversion.employmentType || undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `${fullName(subcontractor.userAccount)} is now an employee`,
          })
          navigate(`/employees/${res.data.userId}`)
        } else if (res.data?.errorCode === 'baseLocationRequired') {
          setConversionBaseError(res.data.error)
        } else if (res.data?.limitType === 'employees') {
          setToast({
            severity: 'error',
            message: `You have reached your employee limit (${res.data.used} of ${res.data.limit}). Add more employee seats from Billing first.`,
          })
        } else {
          setToast({
            severity: 'error',
            message: res.data?.error || 'Unable to convert to an employee',
          })
        }
      })
      .finally(() => setConverting(false))
  }

  const resendVerification = () => {
    API.post(`/subcontractors/${subcontractorId}/resend-verification`).then(
      (res) => {
        if (res.status === 200) applyUpdate(res.data, 'Verification email sent')
      },
    )
  }

  if (loading) return <Loading />
  if (notFound || !subcontractor) {
    return <Alert severity="error">Subcontractor Not Found</Alert>
  }

  const user = subcontractor.userAccount
  const access = subcontractor.access ?? {}
  const isArchived = subcontractor.status === 'Archived'
  const isEmployee = !!user?.employee
  const isPending = subcontractor.status === 'Pending Verification'
  const isIndefinite =
    subcontractor.status === 'Active' && access.validFrom && !access.validUntil

  return (
    <Stack gap={2}>
      <Box>
        <Button
          component={Link}
          to="/employees/subcontractors"
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          All Subcontractors
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" gap={2} alignItems="center">
            <Avatar src={user?.photograph} sx={{ width: 56, height: 56 }}>
              {user?.firstName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="title2">{fullName(user)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Chip
              size="small"
              label={subcontractor.clinicalRole}
              sx={{ borderRadius: 1 }}
            />
            <Chip
              size="small"
              color={statusColor(subcontractor)}
              label={subcontractor.status}
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {isEmployee && (
        <Alert
          severity="info"
          action={
            <Button
              component={Link}
              to={`/employees/${user._id}`}
              color="inherit"
              size="small"
            >
              View Employee
            </Button>
          }
        >
          {fullName(user)} is now an employee
          {subcontractor.convertedToEmployee?.at
            ? ` (converted ${formatDate(subcontractor.convertedToEmployee.at)})`
            : ''}
          . This subcontractor record is kept for the shifts they worked as a
          subcontractor.
        </Alert>
      )}

      {isIndefinite && (
        <Alert severity="warning" icon={<WarningAmberIcon />}>
          This subcontractor has indefinite access. Their account will stay
          active until access is revoked or the account is archived - review it
          regularly and revoke access when the engagement ends to avoid
          permitting unauthorised access.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, height: '100%' }}
          >
            <Stack gap={1.5}>
              <Typography variant="title3">Access</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    label="Valid From"
                    value={formatDate(access.validFrom)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    label="Valid Until"
                    value={
                      access.validUntil
                        ? formatDate(access.validUntil)
                        : access.validFrom
                          ? 'Never expires'
                          : '-'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    label="Last Activated By"
                    value={fullName(access.activatedBy)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    label="Last Activated"
                    value={formatDate(access.activatedAt)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    label="Created By"
                    value={fullName(subcontractor.createdBy)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    label="Created"
                    value={formatDate(subcontractor.createdAt)}
                  />
                </Grid>
              </Grid>
              {!isArchived && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => setGrantOpen(true)}
                  >
                    {subcontractor.status === 'Active'
                      ? 'Extend Access'
                      : 'Grant Access'}
                  </Button>
                  {subcontractor.status === 'Active' && (
                    <Button
                      color="error"
                      variant="outlined"
                      startIcon={<BlockIcon />}
                      onClick={() => setRevokeOpen(true)}
                    >
                      Revoke Access
                    </Button>
                  )}
                  {isPending && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={resendVerification}
                    >
                      Resend Verification
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, p: 2, height: '100%' }}
          >
            <Stack gap={2}>
              <Typography variant="title3">Permissions</Typography>
              <TextField
                select
                label="Clinical role"
                value={clinicalRole}
                onChange={(e) => setClinicalRole(e.target.value)}
                helperText="Controls which shifts and clinical duties this subcontractor can be assigned to."
                fullWidth
              >
                {[
                  ...roles,
                  ...(clinicalRole && !roles.includes(clinicalRole)
                    ? [clinicalRole]
                    : []),
                ].map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
              <Box>
                <Button
                  variant="contained"
                  disabled={
                    !clinicalRole || clinicalRole === subcontractor.clinicalRole
                  }
                  onClick={saveClinicalRole}
                >
                  Save Clinical Role
                </Button>
              </Box>

              <Typography variant="title3">Account</Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {!isEmployee && (
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={openConvert}
                  >
                    Convert to Employee
                  </Button>
                )}
                {isEmployee ? null : isArchived ? (
                  <Button
                    variant="outlined"
                    startIcon={<UnarchiveIcon />}
                    onClick={unarchive}
                  >
                    Unarchive
                  </Button>
                ) : (
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<ArchiveIcon />}
                    onClick={archive}
                  >
                    Archive Subcontractor
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} fullWidth>
        <DialogTitle>
          {subcontractor.status === 'Active' ? 'Extend Access' : 'Grant Access'}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Access duration"
              value={grantDays}
              onChange={(e) => setGrantDays(e.target.value)}
              helperText={`The organisation default is ${
                defaultWindowDays === 0
                  ? 'indefinite access'
                  : `${defaultWindowDays} day${defaultWindowDays === 1 ? '' : 's'}`
              }. The window starts now and replaces any existing window.`}
              fullWidth
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {grantDays === 0 && (
              <Alert severity="warning">
                Indefinite access never expires. This account will be able to
                log in until access is revoked or the account is archived -
                forgotten accounts can permit unauthorised access to patient and
                operational data. Make sure someone owns reviewing this account.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={grantDays === 0 ? 'warning' : 'primary'}
            onClick={grantAccess}
          >
            {grantDays === 0 ? 'Grant Indefinite Access' : 'Grant Access'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={convertOpen}
        onClose={() => !converting && setConvertOpen(false)}
        fullWidth
      >
        <DialogTitle>Convert to Employee</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {fullName(user)} keeps their login. They become a New Starter
              employee, use one of your employee seats, and this subcontractor
              record is archived. Compliance records (DBS, training, licence)
              start empty on the employee.
            </Typography>
            <EmployeeBaseLocationsSelect
              value={conversion.permittedBaseLocations ?? []}
              onChange={(ids) => {
                setConversionBaseError(null)
                setConversion((prev) => ({
                  ...prev,
                  permittedBaseLocations: ids,
                }))
              }}
              error={!!conversionBaseError}
              helperText={
                conversionBaseError ??
                'The base location(s) this employee works from'
              }
            />
            <TextField
              select
              label="Clinical role"
              value={conversion.clinicalRole ?? ''}
              onChange={(e) =>
                setConversion((prev) => ({
                  ...prev,
                  clinicalRole: e.target.value,
                }))
              }
              fullWidth
            >
              {[
                ...roles,
                ...(conversion.clinicalRole &&
                !roles.includes(conversion.clinicalRole)
                  ? [conversion.clinicalRole]
                  : []),
              ].map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
            <AnDateField
              date={conversion.employmentStartDate ?? null}
              onChange={(newDate) =>
                setConversion((prev) => ({
                  ...prev,
                  employmentStartDate: newDate,
                }))
              }
              fullWidth
              label="Employment Start Date"
            />
            <TextField
              select
              label="Employment type"
              value={conversion.employmentType ?? ''}
              onChange={(e) =>
                setConversion((prev) => ({
                  ...prev,
                  employmentType: e.target.value,
                }))
              }
              helperText="Can be set later on the employee's Payroll tab."
              fullWidth
            >
              <MenuItem value="">Not set</MenuItem>
              {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            {conversionPreview && (
              <Alert severity="info">
                {conversionPreview.upcomingShifts
                  ? `${conversionPreview.upcomingShifts} upcoming shift${
                      conversionPreview.upcomingShifts === 1 ? '' : 's'
                    } will move to their employee record.`
                  : 'They have no upcoming shifts to move.'}
                {conversionPreview.pastShifts
                  ? ` ${conversionPreview.pastShifts} past shift${
                      conversionPreview.pastShifts === 1 ? '' : 's'
                    } stay recorded as worked by a subcontractor.`
                  : ''}
              </Alert>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!conversion.sendEmail}
                  onChange={(e) =>
                    setConversion((prev) => ({
                      ...prev,
                      sendEmail: e.target.checked,
                    }))
                  }
                />
              }
              label="Email them to let them know"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={converting} onClick={() => setConvertOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={converting || !conversion.clinicalRole}
            onClick={convertToEmployee}
          >
            Convert to Employee
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)} fullWidth>
        <DialogTitle>Revoke Access</DialogTitle>
        <DialogContent>
          <Typography>
            This immediately ends {fullName(user)}&apos;s access, including any
            active session. They will not be able to log in again until access
            is re-granted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={revokeAccess}>
            Revoke Access
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default SubcontractorSingle
